import { errorAlert } from '@/components/others/ToastGroup';
import {
  DEFAI_STAKE_INFO_SEED,
  DEFAI_STAKE_VAULT_SEED,
  DEFAULT_TOKEN_IDO_CURVE,
  FUNGIBLE_STAKE_CONFIG_SEED,
  FUNGIBLE_VAULT_SEED,
  MEME_STAKE_INFO_SEED,
  MEME_STAKE_VAULT_SEED,
  MODE_COIN,
  PARRY_STATUS,
  SEED_CURVE_VAULT,
  SEED_GLOBAL,
  SPL_DECIMAL,
} from '@/config';
import { SimpleSnapshotContractQueryClient as AgentsLandSnapshotContractQueryClient } from '@/sdk/oraiAgentSdk';
import {
  rayBuyTx,
  raySellTx,
  simulateSwapOnRaydium,
} from '@/utils/raydiumSwap/raydiumSwap';
import {
  ClaimableInfo,
  LaunchDataInfo,
  MetadataInfo,
  StakeInfo,
} from '@/utils/types';
import {
  calculateMarketCap,
  calculateTokenPrice,
  simulateOutIdo,
  toBN,
  toPublicKey,
} from '@/utils/util';
import * as anchor from '@coral-xyz/anchor';
import { BN, Program } from '@coral-xyz/anchor';
import { CosmWasmClient } from '@cosmjs/cosmwasm-stargate';
import { Metaplex } from '@metaplex-foundation/js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import { WalletContextState } from '@solana/wallet-adapter-react';
import {
  ComputeBudgetProgram,
  Keypair,
  LAMPORTS_PER_SOL,
  PublicKey,
  Transaction,
} from '@solana/web3.js';
import BigNumber from 'bignumber.js';
import { ALL_CONFIGS } from '../config';
import { fetchJSONDataFromUrl } from './logListeners/utils';
import { DefaiLaunchpad } from './defai_launchpad/defai_launchpad';
import idl from './defai_launchpad/defai_launchpad.json';
import {
  DCA_POLICY_METADATA_SEED,
  SEED_BONDING_CURVE,
  SEED_CONFIG,
} from './seed';
import { handleTransaction } from './utils';
import { getSupportTokenDefAIOld } from '@/utils/constants';

export type ConfigLaunchPadDefai = anchor.IdlAccounts<DefaiLaunchpad>['config'];

export const commitmentLevel = 'confirmed';
export const TOKEN_RESERVES = 1_000_000_000_000_000;
export const LAMPORT_RESERVES = 1_000_000_000;

export const pumpProgramId = new PublicKey(idl.address);
export const pumpProgramInterface = idl as DefaiLaunchpad;

let globalLaunchpadConfig: ConfigLaunchPadDefai;
export class Web3DefaiProgramInteraction {
  constructor() {}

  private async getLaunchpadGlobalConfig() {
    if (!globalLaunchpadConfig) {
      const provider = anchor.getProvider();
      const program = new Program(
        pumpProgramInterface as DefaiLaunchpad,
        provider,
      ) as Program<DefaiLaunchpad>;

      const [configPda, _] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_CONFIG)],
        program.programId,
      );

      console.log('configPda', configPda);

      const configAccount = await program.account.config.fetch(configPda);
      globalLaunchpadConfig = configAccount;
    }
    return globalLaunchpadConfig;
  }

  // Send Fee to the Fee destination
  createToken = async (
    wallet: WalletContextState,
    coinData: LaunchDataInfo,
    mintKp: Keypair,
  ) => {
    const provider = anchor.getProvider();
    const program = new Program(
      pumpProgramInterface as DefaiLaunchpad,
      provider,
    ) as Program<DefaiLaunchpad>;

    // check the connection
    if (!wallet.publicKey || !provider.connection) {
      errorAlert('Wallet Not Connected');
      console.log('Warning: Wallet not connected');
      return 'WalletError';
    }

    try {
      console.log('coinData--->', coinData);

      const configAccount = await this.getLaunchpadGlobalConfig();
      const creatorTokenAccount = this.getAssociatedTokenAccount(
        wallet.publicKey,
        mintKp.publicKey,
      );
      const stakingTokenAccount = this.getAssociatedTokenAccount(
        configAccount.stakingPoolWallet,
        mintKp.publicKey,
      );
      const communityPoolTokenAccount = this.getAssociatedTokenAccount(
        configAccount.communityPoolWallet,
        mintKp.publicKey,
      );

      const agentTokenAccount = this.getAssociatedTokenAccount(
        new PublicKey(coinData.metadata.agentAddress),
        mintKp.publicKey,
      );

      const [vaultPDA] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_CURVE_VAULT), mintKp.publicKey.toBytes()],
        program.programId,
      );

      const vaultTokenAccount = this.getAssociatedTokenAccount(
        vaultPDA,
        mintKp.publicKey,
      );

      const transaction = new Transaction();
      const setComputeUnitLimit =
        anchor.web3.ComputeBudgetProgram.setComputeUnitLimit({
          units: 1_000_000,
        });
      const setComputePriceLimit =
        anchor.web3.ComputeBudgetProgram.setComputeUnitPrice({
          microLamports: 500_000,
        });

      let totalCurve = new BN((coinData.curveLimit || 0) * 10 ** 9).add(
        new BN(configAccount.lamportAmountConfig || 0),
      );

      if (coinData.isIdoLaunch) {
        totalCurve = new BN((coinData.curveLimit || 0) * 10 ** 9);
      }

      let createIx;
      if (coinData.isIdoLaunch) {
        console.log('153---', coinData);
        createIx = await program.methods
          .idoLaunch(
            coinData.name,
            coinData.symbol,
            coinData.uri,
            totalCurve,
            new BN(coinData.fundsExpiration),
            new BN(coinData.whitelistRoundPeriod * 3600),
            new BN(coinData.partyLimit * 10 ** 9)
              .mul(new BN(10 ** 6))
              .div(new BN(100)),
          )
          .accounts({
            creator: wallet.publicKey,
            token: mintKp.publicKey,
          })
          .remainingAccounts([
            {
              isWritable: true,
              isSigner: false,
              pubkey: configAccount.communityPoolWallet,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: new PublicKey(coinData.metadata.agentAddress),
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: configAccount.stakingPoolWallet,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: vaultTokenAccount,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: agentTokenAccount,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: creatorTokenAccount,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: stakingTokenAccount,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: communityPoolTokenAccount,
            },
          ])
          .instruction();
      } else {
        createIx = await program.methods
          .launch(
            coinData.name,
            coinData.symbol,
            coinData.uri,
            totalCurve,
            new BN((coinData.whitelistRoundPeriod || 0) * 3600),
            new BN(coinData.fundsExpiration || 0),
            new BN((coinData.partyLimit || 0) * 10 ** 9)
              .mul(new BN(10 ** 6))
              .div(new BN(100)),
          )
          .accounts({
            creator: wallet.publicKey,
            token: mintKp.publicKey,
          })
          .remainingAccounts([
            {
              isWritable: true,
              isSigner: false,
              pubkey: configAccount.communityPoolWallet,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: new PublicKey(coinData.metadata.agentAddress),
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: configAccount.stakingPoolWallet,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: vaultTokenAccount,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: agentTokenAccount,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: creatorTokenAccount,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: stakingTokenAccount,
            },
            {
              isWritable: true,
              isSigner: false,
              pubkey: communityPoolTokenAccount,
            },
          ])
          .instruction();
      }

      console.log('createIx', createIx);

      transaction.add(setComputePriceLimit, setComputeUnitLimit, createIx);

      transaction.feePayer = wallet.publicKey;
      const blockhash = await provider.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash.blockhash;

      transaction.sign(mintKp);
      console.log('--------------------------------------');
      console.log(transaction);

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(transaction);
        const sTx = signedTx.serialize();

        console.log(
          '---- simulate tx ----',
          await provider.connection.simulateTransaction(signedTx),
        );
        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: true,
        });

        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'finalized',
        );
        console.log('Successfully initialized.\n Signature: ', signature);
        return {
          ...coinData,
          token: mintKp.publicKey,
          result: res,
        };
      }
    } catch (error) {
      console.log('--error--', error);

      return false;
    }
  };

  // Send Fee to the Fee destination
  submitPolicy = async (
    wallet: WalletContextState,
    coinData: LaunchDataInfo,
    tokenMint: PublicKey,
  ) => {
    const provider = anchor.getProvider();
    const program = new Program(
      pumpProgramInterface as DefaiLaunchpad,
      provider,
    ) as Program<DefaiLaunchpad>;

    // check the connection
    if (!wallet.publicKey || !provider.connection) {
      errorAlert('Wallet Not Connected');
      console.log('Warning: Wallet not connected');
      return 'WalletError';
    }
    let publicKeyDcaErr = false;

    try {
      console.log('DCA data--->', coinData?.dcaConfig);
      const [bondingCurvePda] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_BONDING_CURVE), tokenMint.toBytes()],
        program.programId,
      );
      const curveAccount = await program.account.bondingCurve.fetch(
        bondingCurvePda,
      );

      const transaction = new Transaction();
      const setComputeUnitLimit =
        anchor.web3.ComputeBudgetProgram.setComputeUnitLimit({
          units: 1_000_000,
        });
      const setComputePriceLimit =
        anchor.web3.ComputeBudgetProgram.setComputeUnitPrice({
          microLamports: 500_000,
        });

      transaction.add(setComputePriceLimit, setComputeUnitLimit);

      const listConfig = coinData.dcaConfig?.filter((c) => !!c.token) || [];

      const numberOfDCA = listConfig.length;

      if (!numberOfDCA) {
        // errorAlert('Missing Strategy Config.');
        return 'MissingConfig';
      }

      const totalFund = curveAccount.curveLimit
        .sub(curveAccount.initLamport)
        .sub(curveAccount.migrationAmount);
      const fundAmount = totalFund.div(new BN(numberOfDCA));

      const listIx = await Promise.all(
        listConfig.map(async (e) => {
          try {
            const tokenToDca = new PublicKey(e.token);
          } catch (error) {
            console.log('error convert Publickey', error);
            publicKeyDcaErr = true;
          }

          const dcaStrategyIx = await program.methods
            .registerDcaPolicy(
              fundAmount,
              new BN(
                Number(e.interval || 0) * ALL_CONFIGS.TIMER.DAY_TO_SECONDS,
              ),
              new BN(e.minTokenPrice || 0),
              new BN(e.maxTokenPrice || 0),
            )
            .accounts({
              creator: wallet.publicKey,
              tokenMint,
              tokenToDca: new PublicKey(e.token),
            })
            .instruction();
          return dcaStrategyIx;
        }),
      );

      console.log('listIx', listIx);

      listIx.map((ix) => transaction.add(ix));

      transaction.feePayer = wallet.publicKey;
      const blockhash = await provider.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash.blockhash;

      console.log('--------------------------------------');
      console.log(transaction);

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(transaction);
        const sTx = signedTx.serialize();

        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: true,
        });

        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'finalized',
        );
        console.log('Successfully initialized.\n Signature: ', signature);
        return {
          ...coinData,
          token: tokenMint,
          result: res,
        };
      }
    } catch (error) {
      console.log('--error--', error);
      if (publicKeyDcaErr) {
        errorAlert("Token CA is not valid, please check token's address");
      }

      return false;
    }
  };

  getMaxBondingCurveLimit = async (
    mint: PublicKey,
    isIdo?: boolean,
    priceToken?: number | string,
  ): Promise<any> => {
    const provider = anchor.getProvider();
    // check the connection
    if (!provider.connection) {
      console.log('Warning: connection not connected');
      return;
    }
    const program = new Program(
      pumpProgramInterface,
      provider,
    ) as Program<DefaiLaunchpad>;

    try {
      const configAccount = await this.getLaunchpadGlobalConfig();

      const platformBuyFee = configAccount.platformBuyFee || 0;
      const [bondingCurvePda] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_BONDING_CURVE), mint.toBytes()],
        program.programId,
      );
      const curveAccount = await program.account.bondingCurve.fetch(
        bondingCurvePda,
      );
      const curveLimit = curveAccount.curveLimit.toNumber();

      const solReserve = curveAccount.reserveLamport.toNumber();
      const maxSolSwap = curveLimit - solReserve;

      if (!isIdo) {
        const maxSolSwapIncludeFee = new BigNumber(maxSolSwap)
          .multipliedBy(new BigNumber(platformBuyFee).plus(100))
          .div(100)
          .toNumber();

        return { curveAccount, maxSolSwapIncludeFee };
      } else {
        const remainToken = toBN(curveAccount.partyAmount.toNumber())
          .minus(
            toBN(curveAccount.initToken.toNumber()).minus(
              curveAccount.reserveToken.toNumber(),
            ),
          )
          .div(10 ** 6)
          .toNumber();

        // const remainToken = toBN(DEFAULT_TOKEN_IDO_CURVE * 10 ** 12)
        //   .minus(
        //     toBN((curveAccount.initToken || 0).toString()).minus(
        //       (curveAccount.reserveToken || 0).toString(),
        //     ),
        //   )
        //   .div(10 ** 6)
        //   .toNumber();

        const maxSolSwapIncludeFee = simulateOutIdo(
          mint,
          remainToken,
          priceToken || 0,
        );

        return { curveAccount, maxSolSwapIncludeFee };
      }
    } catch (error) {
      console.log('---------Error in get config curve limit', error);
      return { curveAccount: {}, maxSolSwapIncludeFee: 0 };
    }
  };

  getBondingCurveOfToken = async (mint: PublicKey): Promise<any> => {
    const provider = anchor.getProvider();
    // check the connection
    if (!provider.connection) {
      console.log('Warning: connection not connected');
      return;
    }
    const program = new Program(
      pumpProgramInterface,
      provider,
    ) as Program<DefaiLaunchpad>;

    try {
      const [bondingCurvePda] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_BONDING_CURVE), mint.toBytes()],
        program.programId,
      );
      const curveInfo = await program.account.bondingCurve.fetch(
        bondingCurvePda,
      );

      console.log('curveInfo', curveInfo);

      return curveInfo;
    } catch (error) {
      console.log('Error in get config curve limit', error);
      return;
    }
  };

  getConfigData = async (wallet: WalletContextState): Promise<any> => {
    const provider = anchor.getProvider();
    // check the connection
    if (!provider.connection) {
      console.log('Warning: connection not connected');
      return;
    }
    const program = new Program(
      pumpProgramInterface,
      provider,
    ) as Program<DefaiLaunchpad>;

    try {
      const configAccount = await this.getLaunchpadGlobalConfig();

      const platformBuyFee = configAccount.platformBuyFee;
      return platformBuyFee;
    } catch (error) {
      console.log('Error in get config curve limit', error);
      return 0;
    }
  };

  // Swap transaction
  swapTx = async (
    mint: PublicKey,
    wallet: WalletContextState,
    amount: string,
    type: number,
    simulateReceive: string,
    slippage: string,
    isParty?: boolean,
    isIdoLaunch?: boolean,
  ): Promise<any> => {
    console.log('==============trade swap==============', type);

    let provider;
    try {
      provider = anchor.getProvider();
      // check the connection
      if (!wallet.publicKey || !provider.connection) {
        console.log('Warning: Wallet not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;

      const configAccount = await this.getLaunchpadGlobalConfig();
      const [bondingCurvePda] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_BONDING_CURVE), mint.toBytes()],
        program.programId,
      );
      const curveAccount = await program.account.bondingCurve.fetch(
        bondingCurvePda,
      );
      const curveLimit = curveAccount.curveLimit.toNumber();

      const solReserve = curveAccount.reserveLamport.toNumber();

      const maxSolSwap = curveLimit - solReserve;
      console.log(maxSolSwap, amount);

      const transaction = new Transaction();
      const cpIx = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: 1_000_000,
      });
      const cuIx = ComputeBudgetProgram.setComputeUnitLimit({ units: 200_000 });

      const coinDecimal = type === 0 ? 9 : 6;
      const receiveDecimal = type !== 0 ? 9 : 6;
      const addFee = type === 0 ? slippage : Number(slippage) + 1; // fee
      const fmtAmount = new anchor.BN(
        parseFloat(amount) * Math.pow(10, coinDecimal),
      );

      // FIXME: uncomment to check limit curve
      // if (
      //   type === 0 &&
      //   new BigNumber(fmtAmount.toNumber()).isGreaterThan(maxSolSwap)
      // ) {
      //   console.log("Exceeded bonding curve limit");
      //   throw Error("Exceeded bonding curve limit");
      // }

      const minAmount = toBN(simulateReceive)
        .multipliedBy(Math.pow(10, receiveDecimal))
        .multipliedBy(toBN(1).minus(Number(addFee) / 100))
        .toNumber();

      let swapIx;

      if (isIdoLaunch && !isParty) {
        const tokenAccount = this.getAssociatedTokenAccount(
          wallet.publicKey,
          mint,
        );
        // buy sell ido
        swapIx = await program.methods
          .idoSwap(fmtAmount, type)
          .accounts({
            teamWallet: configAccount.teamWallet,
            user: wallet.publicKey,
            tokenMint: mint,
          })
          .remainingAccounts([
            {
              isWritable: true,
              isSigner: false,
              pubkey: tokenAccount,
            },
          ])
          .instruction();
      } else if (!isIdoLaunch && !isParty) {
        // buy sell normal
        const { isSupport } = getSupportTokenDefAIOld(mint.toString());
        const method = isSupport ? 'swapLegacy' : 'swap';
        let params = {
          teamWallet: configAccount.teamWallet,
          user: wallet.publicKey,
          tokenMint: mint,
        };

        if (isSupport) {
          params['creatorWallet'] = curveAccount.creator;
          params['tokenProgram'] = TOKEN_PROGRAM_ID;
        }

        swapIx = await program.methods[method](
          fmtAmount,
          type,
          new anchor.BN(minAmount || 0),
        )
          .accounts(params)
          .instruction();
      } else if (!isIdoLaunch && isParty) {
        // buy sell normal in party
        swapIx = await program.methods
          .buyParty(fmtAmount, type, new anchor.BN(minAmount || 0))
          .accounts({
            teamWallet: configAccount.teamWallet,
            user: wallet.publicKey,
            tokenMint: mint,
          })
          .instruction();
      } else if (isIdoLaunch && isParty) {
        // buy sell ido party
        swapIx = await program.methods
          .idoBuyParty(fmtAmount, type)
          .accounts({
            teamWallet: configAccount.teamWallet,
            user: wallet.publicKey,
            tokenMint: mint,
          })
          .instruction();
      }

      transaction.add(swapIx);
      transaction.add(cpIx, cuIx);
      transaction.feePayer = wallet.publicKey;
      transaction.recentBlockhash = (
        await provider.connection.getLatestBlockhash()
      ).blockhash;

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(transaction);
        const sTx = signedTx.serialize();
        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: false,
        });
        const blockhash = await provider.connection.getLatestBlockhash();

        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'confirmed', // FIXME: trick lord confirmed / finalized;
        );

        console.log('Successfully initialized.\n Signature: ', signature);
        return res;
      }
    } catch (error) {
      console.log('Error in swap transaction', error, error.error);

      if (!provider) {
        return;
      }
      const { transaction = '', result } =
        (await handleTransaction({
          error,
          connection: provider.connection,
        })) || {};

      if (result?.value?.confirmationStatus) {
        console.log('----confirm----', { transaction, result });
        return { transaction, result };
      }
    }
  };

  //Raydium Swap transaction
  raydiumSwapTx = async (
    mint: PublicKey,
    wallet: WalletContextState,
    amount: string,
    type: number,
    poolKey: string,
    slippage: string,
  ) => {
    let provider;
    try {
      provider = anchor.getProvider();
      // check the connection
      if (!wallet.publicKey || !provider.connection) {
        console.log('Warning: Wallet not connected');
        return;
      }

      const poolId = new PublicKey(poolKey);
      const coinDecimal = type === 0 ? 9 : 6;
      const fmtAmount = new anchor.BN(
        parseFloat(amount) * Math.pow(10, coinDecimal),
      ).toNumber();
      const transaction = new Transaction();

      let swapTx;
      if (type == 0) {
        swapTx = await rayBuyTx(
          provider.connection,
          mint,
          fmtAmount,
          wallet,
          poolId,
          slippage,
        );
      } else {
        swapTx = await raySellTx(
          provider.connection,
          mint,
          fmtAmount,
          wallet,
          poolId,
          slippage,
        );
      }
      if (swapTx == null) {
        console.log(`Error getting buy transaction`);
        return null;
      }

      // transaction.add(cpIx, cuIx);
      transaction.add(...swapTx.instructions);

      transaction.feePayer = wallet.publicKey;
      transaction.recentBlockhash = (
        await provider.connection.getLatestBlockhash()
      ).blockhash;

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(transaction);
        const sTx = signedTx.serialize();
        // console.log(
        //   "----",
        //   await this.connection.simulateTransaction(signedTx)
        // );
        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: false,
        });
        const blockhash = await provider.connection.getLatestBlockhash();
        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'confirmed',
        );
        console.log('Successfully initialized.\n Signature: ', signature);
        return res;
      }
    } catch (error) {
      console.log('Error in swap transaction', error, error.error);
      if (!provider) {
        return;
      }
      const { transaction = '', result } =
        (await handleTransaction({
          error,
          connection: provider.connection,
        })) || {};

      if (result?.value?.confirmationStatus) {
        console.log('----confirm----raydium', { transaction, result });
        return { transaction, result };
      }
    }
  };

  //Raydium Swap transaction
  simulateRaydiumSwapTx = async (
    mint: PublicKey,
    wallet: WalletContextState,
    amount: string,
    type: number,
    poolKey: string,
  ) => {
    try {
      const provider = anchor.getProvider();
      if (!poolKey) {
        console.log('Coin not listed');
        return;
      }

      // check the connection
      if (!wallet.publicKey || !provider.connection) {
        console.log('Warning: Wallet not connected');
        return;
      }
      const poolId = new PublicKey(poolKey);
      const res = await simulateSwapOnRaydium(type, {
        solanaConnection: provider.connection,
        baseMint: mint,
        amount: Number(amount),
        wallet,
        poolId,
      });

      if (res) {
        const { numerator, denominator } = res;

        // return new anchor.BN(denominator).eqn(0)
        //   ? 0
        //   : new anchor.BN(numerator).div(denominator).toNumber();

        // return numerator.toString();
        return res;
      }

      // return "0";
    } catch (error) {
      console.log('Error in simulate swap transaction', error);
    }
  };

  // Swap transaction
  simulateSwapTx = async (
    mint: PublicKey,
    wallet: WalletContextState,
    amount: string,
    type: number,
  ): Promise<string> => {
    console.log('========Simulate swap==============');

    const provider = anchor.getProvider();
    // check the connection
    if (!wallet.publicKey || !provider.connection) {
      console.log('Warning: Wallet not connected');
      return;
    }

    const program = new Program(
      pumpProgramInterface,
      provider,
    ) as Program<DefaiLaunchpad>;

    try {
      const tx = await program.methods
        .simulateSwap(new BN(amount), type)
        .accounts({
          tokenMint: mint,
        })
        .view();

      const actualAmountOut = new BN(tx).toString();
      return actualAmountOut;
    } catch (error) {
      console.log('Error in simulate swap transaction', error, error.error);
    }
  };

  getTokenBalance = async (walletAddress: string, tokenMintAddress: string) => {
    try {
      const wallet = new PublicKey(walletAddress);
      const tokenMint = new PublicKey(tokenMintAddress);

      const provider = anchor.getProvider();
      // Fetch the token account details
      const response = this.getAssociatedTokenAccount(wallet, tokenMint);

      // Get the balance
      const tokenAccountInfo = await provider.connection.getTokenAccountBalance(
        response,
      );

      return tokenAccountInfo.value.uiAmount;
    } catch (error) {
      return 0;
    }
  };

  getSolanaBalance = async (publicKey: PublicKey) => {
    try {
      const provider = anchor.getProvider();
      const balance = await provider.connection.getBalance(publicKey);
      const balanceSolana = new BigNumber(balance)
        .dividedBy(LAMPORTS_PER_SOL)
        .toNumber();

      return balanceSolana;
    } catch (error) {
      return 0;
    }
  };

  getNumberOfOwnedToken = async (walletPublicKey: PublicKey) => {
    try {
      const provider = anchor.getProvider();
      // // Convert the wallet address to PublicKey
      // const walletPublicKey = new PublicKey(walletAddress);

      // Fetch all token accounts owned by the wallet
      const tokenAccounts =
        await provider.connection.getParsedTokenAccountsByOwner(
          walletPublicKey,
          {
            programId: new PublicKey(
              'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
            ), // SPL Token Program ID
          },
        );

      // Process token accounts to list balances and mint addresses
      const tokens = tokenAccounts.value
        .map((tokenAccount) => {
          const info = tokenAccount.account.data.parsed.info;
          const mint = info.mint; // Mint address (unique for each token)
          const balance = info.tokenAmount.uiAmount; // Human-readable balance

          return { mint, balance };
        })
        .filter((token) => token.balance > 0); // Filter out zero-balance tokens

      // Count unique tokens and log the results
      const uniqueTokens = new Set(tokens.map((token) => token.mint));
      console.log(`You hold ${uniqueTokens.size} unique tokens:`);
      // tokens.forEach((token) => {
      //   console.log(`Token Mint: ${token.mint}, Balance: ${token.balance}`);
      // });

      return {
        uniqueTokenCount: uniqueTokens.size,
        tokenDetails: tokens,
      };
    } catch (error) {
      console.error('Error fetching token balances:', error);
      return {
        uniqueTokenCount: 0,
        tokenDetails: [],
      };
    }
  };

  getAssociatedTokenAccount = (
    ownerPubkey: PublicKey,
    mintPk: PublicKey,
  ): PublicKey => {
    let associatedTokenAccountPubkey = PublicKey.findProgramAddressSync(
      [
        ownerPubkey.toBytes(),
        TOKEN_PROGRAM_ID.toBytes(),
        mintPk.toBytes(), // mint address
      ],
      ASSOCIATED_TOKEN_PROGRAM_ID,
    )[0];

    return associatedTokenAccountPubkey;
  };

  getConfigGlobal = async (wallet, token) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;

      const [global_vault] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_GLOBAL)],
        program.programId,
      );
      const globalVaultTokenAccount = this.getAssociatedTokenAccount(
        global_vault,
        token,
      );
      const globalVaultBalance =
        await provider.connection.getTokenAccountBalance(
          globalVaultTokenAccount,
        );

      return globalVaultBalance;
    } catch (error) {
      console.log('getConfigGlobal error', error);
    }
  };

  getBondingAddressToken = async (wallet) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;

      const [global_vault] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_GLOBAL)],
        program.programId,
      );

      return global_vault.toBase58();
    } catch (error) {
      console.log('error', error);
      return '';
    }
  };

  getSnapShotAgent = async () => {
    try {
      const oraiEndpoint = 'https://rpc.orai.io';
      const whitelistContractAddress =
        'orai14z64p3yp8rv99ewvycpeef7h4jlyqwmpyt63m86wyyh0dhjxhqescyclm0';
      const cwClient = await CosmWasmClient.connect(oraiEndpoint);
      const contract = new AgentsLandSnapshotContractQueryClient(
        cwClient,
        whitelistContractAddress,
      );

      // query list token metadata
      /// return:
      /// - token: token addr
      /// - metadata: base64 encode of token metadata
      const dataSnap = await contract.tokensMetadata();

      const res = (dataSnap || []).map((e) => {
        return {
          ...e,
          metadata: JSON.parse(Buffer.from(e.metadata, 'base64').toString()),
        };
      });

      return res || [];
    } catch (error) {
      console.log('error get snapshot', error);
      return [];
    }
  };

  // allowedAddresses: ConfigState['configSnapshot']
  getListTokenFromContract = async () => {
    try {
      const provider = anchor.getProvider();
      const allowedAddresses = await this.getSnapShotAgent();

      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }
      const metaplex = Metaplex.make(provider.connection);
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;

      const tokens = await provider.connection.getParsedProgramAccounts(
        program.programId,
        {
          commitment: 'confirmed',
          filters: [
            {
              dataSize: 224,
            },
          ],
        },
      );
      const configAccount = await this.getLaunchpadGlobalConfig();

      const list = await Promise.all(
        tokens.map(async (item) => {
          const detail = program.coder.accounts.decode<
            anchor.IdlAccounts<DefaiLaunchpad>['bondingCurve']
          >('bondingCurve', item.account.data as Buffer);

          if (
            import.meta.env.VITE_APP_SOLANA_ENV !== 'devnet' &&
            allowedAddresses.length > 0 &&
            !allowedAddresses
              .map((e) => e.token)
              .includes(detail.tokenMint.toBase58())
          ) {
            return;
          }

          const metadata = await metaplex
            .nfts()
            .findByMint(
              { mintAddress: detail.tokenMint },
              { commitment: 'confirmed' },
            );

          let metadataJson: MetadataInfo = {} as any;
          if (metadata.uri) {
            const dataJson = (await fetchJSONDataFromUrl(metadata.uri)) || {};
            metadataJson = dataJson;
          }

          const solPrice = Number(localStorage.getItem('solPrice'));
          const newPrice = calculateTokenPrice(
            detail.reserveToken,
            detail.reserveLamport,
            configAccount.tokenDecimalsConfig || SPL_DECIMAL,
            solPrice,
          );
          const marketcap = calculateMarketCap(
            detail.reserveToken,
            configAccount.tokenDecimalsConfig || SPL_DECIMAL,
            newPrice,
          );

          const tokenDetail = {
            creator: detail.creator.toBase58(),
            decimals: configAccount.tokenDecimalsConfig || SPL_DECIMAL,
            name: metadata.name,
            ticker: metadata.symbol,
            description: metadataJson.description,
            token: detail.tokenMint.toBase58(),
            url: metadataJson.image,
            tokenReserves: detail.reserveToken,
            lamportReserves: detail.reserveLamport,
            marketcap,
            bondingCurveLimit:
              detail?.curveLimit || ALL_CONFIGS.MEME_BONDING_CURVE_LIMIT,
            metadata: {
              ...metadata,
              ...metadataJson,
              agentAddress: metadata.address.toBase58(),
            } as MetadataInfo,
            listed: detail.isCompleted, // TODO: this value in contract is bonding curve isCompleted, but when data BE failed we only need to check isComplete is true and user can trade via raydium
            tradingTime: new Date(
              detail.partyStart.muln(ALL_CONFIGS.TIMER.MILLISECONDS).toNumber(),
            ),
            date: new Date(
              detail.curveCreationDate
                .muln(ALL_CONFIGS.TIMER.MILLISECONDS)
                .toNumber(),
            ),
          };

          return { ...detail, ...tokenDetail };
        }),
      );

      const listFmt = list.filter(Boolean);
      return {
        coins: listFmt,
        total: listFmt.length,
        fromRpc: true,
      };
    } catch (error) {
      console.log('getListTokenFromContract error', error);
      return {
        coins: [],
        total: 0,
      };
    }
  };

  getTokenDetailFromContract = async (
    wallet: WalletContextState,
    tokenMint: PublicKey,
    mode: MODE_COIN,
  ) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }
      const allowedAddresses = await this.getSnapShotAgent();
      const currentTokenData = allowedAddresses.find(
        (e) => e.token === tokenMint.toBase58(),
      );
      const raydiumPoolAddr =
        currentTokenData?.metadata?.['raydiumPoolAddr'] || '';
      const oraidexPoolAddr =
        currentTokenData?.metadata?.['oraidexPoolAddr'] || '';

      const metaplex = Metaplex.make(provider.connection);
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;

      const metadata = await metaplex
        .nfts()
        .findByMint({ mintAddress: tokenMint }, { commitment: 'confirmed' });

      let metadataJson: MetadataInfo = {} as any;
      if (metadata.uri) {
        const dataJson = (await fetchJSONDataFromUrl(metadata.uri)) || {};
        metadataJson = dataJson;
      }
      const [bondingCurvePda, _] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_BONDING_CURVE), tokenMint.toBytes()],
        program.programId,
      );

      const [configAccount, bondingCurve] = await Promise.all([
        this.getLaunchpadGlobalConfig(),
        program.account.bondingCurve.fetch(bondingCurvePda),
      ]);

      const solPrice = Number(localStorage.getItem('solPrice'));
      const newPrice = calculateTokenPrice(
        bondingCurve.reserveToken,
        bondingCurve.reserveLamport,
        SPL_DECIMAL,
        solPrice,
      );
      const marketcap = calculateMarketCap(
        bondingCurve.reserveToken,
        SPL_DECIMAL,
        newPrice,
      );

      const tokenDetail = {
        creator: bondingCurve.creator.toBase58(),
        decimals: SPL_DECIMAL,
        name: metadata.name,
        ticker: metadata.symbol,
        description: metadataJson.description,
        token: tokenMint.toBase58(),
        url: metadataJson.image,
        tokenReserves: bondingCurve.reserveToken.toNumber(),
        lamportReserves: bondingCurve.reserveLamport.toNumber(),
        raydiumPoolAddr,
        oraidexPoolAddr,
        bondingCurveLimit:
          bondingCurve?.curveLimit || ALL_CONFIGS.MEME_INIT_SOL_BONDING_CURVE,
        metadata: {
          ...metadata,
          ...metadataJson,
          agentAddress: metadata.address.toBase58(),
        } as MetadataInfo,
        listed: raydiumPoolAddr || oraidexPoolAddr,
        tradingTime: new Date(
          toBN(bondingCurve.partyStart.toNumber())
            .multipliedBy(ALL_CONFIGS.TIMER.MILLISECONDS)
            .toNumber(),
        ),
        marketcap,
        date: new Date(
          bondingCurve.curveCreationDate
            .muln(ALL_CONFIGS.TIMER.MILLISECONDS)
            .toNumber(),
        ),
      };

      return tokenDetail;
    } catch (error) {
      console.log('getTokenDetailFromContract error', error);
      return;
    }
  };

  getConfigCurve = async (onFailed?: () => void) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }
      if (!provider) {
        return;
      }

      const configAccount = await this.getLaunchpadGlobalConfig();

      return configAccount;
    } catch (error) {
      console.log('getConfigCurve error', error);

      errorAlert(
        'RPC call limit reached. Please wait or switch networks to continue!',
      );
      typeof onFailed === 'function' && onFailed();

      return;
    }
  };

  getCurvePDA = (tokenMint: PublicKey) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;

      const [bondingCurvePda, _] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_BONDING_CURVE), tokenMint.toBytes()],
        program.programId,
      );

      return bondingCurvePda;
    } catch (error) {
      console.log('getCurvePDA', error);
    }
  };

  decodeCurveData = (data: Buffer) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: Wallet not connected');
        return;
      }
      if (!provider) {
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;
      const bondingCurveInfo = program.coder.accounts.decode<
        anchor.IdlAccounts<DefaiLaunchpad>['bondingCurve']
      >('bondingCurve', data as Buffer);

      return bondingCurveInfo;
    } catch (error) {
      console.log('decodeCurveData error', error);
      return '0';
    }
  };

  async stake(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    amount: number,
    wallet: WalletContextState,
  ) {
    let provider;
    try {
      provider = anchor.getProvider();
      if (!provider.connection || !wallet.publicKey) {
        console.log('Warning: Wallet not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;

      const [configPda, _] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_CONFIG)],
        program.programId,
      );
      const configAccount = await program.account.config.fetch(configPda);
      const transaction = new Transaction();
      const cpIx = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: 1_000_000,
      });
      const cuIx = ComputeBudgetProgram.setComputeUnitLimit({ units: 200_000 });

      const stakeIx = await program.methods
        .stake(new BN(amount))
        .accounts({
          signer: wallet.publicKey,
          stakeCurrencyMint:
            configAccount.stakingToken || toPublicKey(stakeCurrencyMint),
          rewardCurrencyMint: toPublicKey(rewardCurrencyMint),
        })
        .instruction();

      transaction.add(stakeIx);
      transaction.add(cpIx, cuIx);
      transaction.feePayer = wallet.publicKey;
      transaction.recentBlockhash = (
        await provider.connection.getLatestBlockhash()
      ).blockhash;

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(transaction);
        const sTx = signedTx.serialize();
        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: false,
        });
        const blockhash = await provider.connection.getLatestBlockhash();

        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'confirmed',
        );

        console.log('Successfully locking token.\n Signature: ', signature);
        return res;
      }
    } catch (error) {
      console.log('Error in locking token transaction', error, error.error);

      if (!provider) {
        return;
      }
      const { transaction = '', result } =
        (await handleTransaction({
          error,
          connection: provider.connection,
        })) || {};

      if (result?.value?.confirmationStatus) {
        console.log('----confirm----', { transaction, result });
        return { transaction, result };
      }
    }
  }

  async unStake(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    amount: number,
    wallet: WalletContextState,
  ) {
    let provider;
    try {
      provider = anchor.getProvider();
      if (!provider.connection || !wallet.publicKey) {
        console.log('Warning: Wallet not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;

      const transaction = new Transaction();
      const cpIx = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: 1_000_000,
      });
      const cuIx = ComputeBudgetProgram.setComputeUnitLimit({ units: 200_000 });

      const [configPda, _] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_CONFIG)],
        program.programId,
      );
      const configAccount = await program.account.config.fetch(configPda);

      const { isSupport } = getSupportTokenDefAIOld(rewardCurrencyMint);
      const method = isSupport ? 'destakeLegacy' : 'destake';

      let params = {
        signer: wallet.publicKey,
        stakeCurrencyMint:
          configAccount.stakingToken || toPublicKey(stakeCurrencyMint),
        rewardCurrencyMint: new PublicKey(rewardCurrencyMint),
      };

      if (isSupport) params['tokenProgram'] = TOKEN_PROGRAM_ID;

      const unStakeIx = await program.methods[method](new BN(amount))
        .accounts(params)
        .instruction();

      transaction.add(unStakeIx);
      transaction.add(cpIx, cuIx);
      transaction.feePayer = wallet.publicKey;
      transaction.recentBlockhash = (
        await provider.connection.getLatestBlockhash()
      ).blockhash;

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(transaction);
        const sTx = signedTx.serialize();
        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: false,
        });
        const blockhash = await provider.connection.getLatestBlockhash();

        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'confirmed',
        );

        console.log('Successfully unlocking token.\n Signature: ', signature);
        return res;
      }
    } catch (error) {
      console.log('Error in locking token transaction', error, error.error);

      if (!provider) {
        return;
      }
      const { transaction = '', result } =
        (await handleTransaction({
          error,
          connection: provider.connection,
        })) || {};

      if (result?.value?.confirmationStatus) {
        console.log('----confirm----', { transaction, result });
        return { transaction, result };
      }
    }
  }

  async getStakeInfoCache(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    wallet: WalletContextState,
    getNew: boolean,
  ) {
    if (!wallet.publicKey) {
      console.log('User not connected');
      return {
        bump: [],
        unstakedAtTime: new BN(0),
        stakeAmount: new BN(0),
        snapshotAmount: new BN(0),
        totalClaim: new BN(0),
        staker: wallet.publicKey,
        vault: null,
      } as anchor.IdlAccounts<DefaiLaunchpad>['stakeInfo'];
    }

    // we attempt to cache stake info query since it is unlikely this data will change, unless users stake multiple times.
    // this would help reduce total # of RPC calls
    const stakeInfoLocalStorageKey = `defai-stakeInfo-${wallet.publicKey.toBase58()}-${stakeCurrencyMint}-${rewardCurrencyMint}}`;
    if (getNew) {
      return await this.getAndStoreStakeInfoToLocalStorage(
        stakeCurrencyMint,
        rewardCurrencyMint,
        wallet,
        stakeInfoLocalStorageKey,
      );
    }
    const stakeInfoJSON = localStorage.getItem(stakeInfoLocalStorageKey);
    if (!stakeInfoJSON) {
      return await this.getAndStoreStakeInfoToLocalStorage(
        stakeCurrencyMint,
        rewardCurrencyMint,
        wallet,
        stakeInfoLocalStorageKey,
      );
    }

    const parsedStakeInfo = JSON.parse(stakeInfoJSON);
    const stakeInfo: StakeInfo = {
      ...parsedStakeInfo,
      snapshotAmount: new BN(parsedStakeInfo.snapshotAmount),
      stakeAmount: new BN(parsedStakeInfo.stakeAmount),
      unstakedAtTime: new BN(parsedStakeInfo.unstakedAtTime),
    };
    return stakeInfo;
  }

  private async getAndStoreStakeInfoToLocalStorage(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    wallet: WalletContextState,
    stakeInfoLocalStorageKey: string,
  ) {
    const stakerInfo = await this.getStakeInfo(
      stakeCurrencyMint,
      rewardCurrencyMint,
      wallet,
    );
    if (!stakerInfo) {
      // fallback default value so it doesn't break the entire page
      return {
        bump: [],
        unstakedAtTime: new BN(0),
        stakeAmount: new BN(0),
        snapshotAmount: new BN(0),
        totalClaim: new BN(0),
        staker: wallet.publicKey,
        vault: null,
      } as anchor.IdlAccounts<DefaiLaunchpad>['stakeInfo'];
    }
    // localStorage.setItem(
    //   stakeInfoLocalStorageKey,
    //   JSON.stringify({
    //     ...stakerInfo,
    //     totalClaim: stakerInfo.totalClaim.toString(),
    //     snapshotAmount: stakerInfo.snapshotAmount.toString(),
    //     stakeAmount: stakerInfo.stakeAmount.toString(),
    //     unstakedAtTime: stakerInfo.unstakedAtTime.toString(),
    //   }),
    // );
    return stakerInfo;
  }

  private async getStakeInfo(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    wallet: WalletContextState,
  ) {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: connection not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;

      let [vaultPda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(DEFAI_STAKE_VAULT_SEED),
          toPublicKey(rewardCurrencyMint).toBytes(),
        ],
        program.programId,
      );
      let [stakerInfoPda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(DEFAI_STAKE_INFO_SEED),
          vaultPda.toBytes(),
          wallet.publicKey.toBytes(),
        ],
        program.programId,
      );
      const stakerInfo = await program.account.stakeInfo.fetch(stakerInfoPda);

      return stakerInfo;
    } catch (error) {
      console.log('Error in get stake token transaction', error, error.error);

      return;
    }
  }

  async getVaultInfoCache(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
  ) {
    // won't fetch vault info if we have reached the end of staking vault, and just query from local storage
    const vaultInfoKey = `reset-defai-vaultInfo-${stakeCurrencyMint}-${rewardCurrencyMint}}`;
    const vaultInfoJSON = localStorage.getItem(vaultInfoKey);
    if (!vaultInfoJSON) {
      return await this.getAndStoreVaultInfoToLocalStorage(
        stakeCurrencyMint,
        rewardCurrencyMint,
        vaultInfoKey,
      );
    }
    const parsedVaultInfo = JSON.parse(vaultInfoJSON);
    const vaultInfo: anchor.IdlAccounts<DefaiLaunchpad>['stakingVault'] = {
      ...parsedVaultInfo,
      totalStaked: new BN(parsedVaultInfo.totalStaked),
      stakeEndTime: new BN(parsedVaultInfo.stakeEndTime),
      totalReward: new BN(parsedVaultInfo.totalReward),
      maxRewardPerWallet: new BN(parsedVaultInfo.maxRewardPerWallet),
    };

    const nowInSecs = Date.now() / 1000;
    // this means the vault has ended -> no need to query vault anymore
    if (
      vaultInfo.stakeEndTime.toNumber() > 0 &&
      nowInSecs > vaultInfo.stakeEndTime.toNumber()
    ) {
      return vaultInfo;
    }
    // else, we update with latest vault info, and cache it into the local storage
    return await this.getAndStoreVaultInfoToLocalStorage(
      stakeCurrencyMint,
      rewardCurrencyMint,
      vaultInfoKey,
    );
  }

  private async getAndStoreVaultInfoToLocalStorage(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
    vaultInfoKey: string,
  ) {
    const vaultInfo = await this.getVaultInfo(
      stakeCurrencyMint,
      rewardCurrencyMint,
    );
    const parsedVaultInfo = {
      ...vaultInfo,
      totalStaked: vaultInfo.totalStaked.toString(),
      stakeEndTime: vaultInfo.stakeEndTime.toString(),
    };

    const nowInSecs = Date.now() / 1000;
    // from party start =>> cached
    // if (nowInSecs > vaultInfo.stakeEndTime.toNumber()) {
    //   localStorage.setItem(vaultInfoKey, JSON.stringify(parsedVaultInfo));
    // }
    return vaultInfo;
  }

  private async getVaultInfo(
    stakeCurrencyMint: string,
    rewardCurrencyMint: string,
  ) {
    let vaultInfo: anchor.IdlAccounts<DefaiLaunchpad>['stakingVault'] = {
      totalStaked: new BN('0'),
      stakeEndTime: new BN('0'),
      bump: [],
      rewardCurrencyMint: null,
      version: 1,
    } as any;
    try {
      const provider = anchor.getProvider();
      if (!provider.connection) {
        console.log('Warning: connection not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;

      const [vaultPda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(MEME_STAKE_VAULT_SEED),
          new PublicKey(rewardCurrencyMint).toBytes(),
        ],
        program.programId,
      );

      const newVaultInfo = await program.account.stakingVault.fetch(vaultPda);
      return newVaultInfo || vaultInfo;
    } catch (error) {
      console.log('Error in get stake vault info', error, error.error);
      return vaultInfo;
    }
  }

  getAmountBoughtByUser = async (
    token: PublicKey,
    wallet: WalletContextState,
  ) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection || !wallet.publicKey) {
        console.log('Warning: Wallet not connected');
        return;
      }
      if (!provider) {
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;
      const [bondingCurvePda] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_BONDING_CURVE), token.toBytes()],
        program.programId,
      );

      const [userPartyPda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(PARRY_STATUS),
          bondingCurvePda.toBytes(),
          wallet.publicKey.toBytes(),
        ],
        program.programId,
      );

      const userPartyInfo = await program.account.stakeInfo.fetch(userPartyPda);

      const amount = (userPartyInfo.totalClaim || '0').toString();

      return amount;
    } catch (error) {
      console.log('getAmountBoughtByUser error', error);
      return '0';
    }
  };

  getPolicyDef = async (publicKey: PublicKey) => {
    try {
      const provider = anchor.getProvider();
      let infoPolicy = [];
      if (!provider.connection) {
        console.log('Warning: Wallet not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;
      const [configPda] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_CONFIG)],
        program.programId,
      );
      const [bondingCurvePda] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_BONDING_CURVE), publicKey.toBytes()],
        program.programId,
      );
      const [policyMetadataPda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(DCA_POLICY_METADATA_SEED),
          configPda.toBuffer(),
          bondingCurvePda.toBuffer(),
        ],
        program.programId,
      );

      const tokensSubscribes =
        await provider.connection.getParsedProgramAccounts(program.programId, {
          commitment: 'confirmed',
          filters: [
            {
              dataSize: 160,
            },
            {
              memcmp: {
                offset: 10, // number of bytes
                bytes: policyMetadataPda.toBase58(), // base58 encoded string
              },
            },
          ],
        });

      for (const subscribeDetail of tokensSubscribes) {
        const info = program.coder.accounts.decode<
          anchor.IdlAccounts<DefaiLaunchpad>['dcaPolicy']
        >('dcaPolicy', subscribeDetail.account.data as Buffer);
        infoPolicy = [...infoPolicy, info];
      }
      return infoPolicy;
    } catch (error) {
      console.log({ error });
      return [];
    }
  };

  getPolicyDefList = async () => {
    try {
      const provider = anchor.getProvider();
      let infoPolicy = [];
      if (!provider.connection) {
        console.log('Warning: Wallet not connected');
        return;
      }
      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;
      const [configPda] = PublicKey.findProgramAddressSync(
        [Buffer.from(SEED_CONFIG)],
        program.programId,
      );

      const tokensSubscribes =
        await provider.connection.getParsedProgramAccounts(program.programId, {
          commitment: 'confirmed',
          filters: [
            {
              dataSize: 160,
            },
          ],
        });

      for (const subscribeDetail of tokensSubscribes) {
        const info = program.coder.accounts.decode<
          anchor.IdlAccounts<DefaiLaunchpad>['dcaPolicy']
        >('dcaPolicy', subscribeDetail.account.data as Buffer);
        infoPolicy = [...infoPolicy, info];
      }
      // console.log('infoPolicy', infoPolicy);
      return infoPolicy;
    } catch (error) {
      console.log({ error });
      return [];
    }
  };

  getClaimableToken = async (
    wallet: WalletContextState,
    tokenAddress: string,
  ) => {
    try {
      const provider = anchor.getProvider();
      if (!provider.connection || !wallet.publicKey) {
        console.log('Warning: Wallet not connected');
        return;
      }

      const program = new Program(
        pumpProgramInterface,
        provider,
      ) as Program<DefaiLaunchpad>;
      let [vaultPda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(DEFAI_STAKE_VAULT_SEED),
          toPublicKey(tokenAddress).toBytes(),
        ],
        program.programId,
      );
      let [userStakePda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from(DEFAI_STAKE_INFO_SEED),
          vaultPda.toBytes(),
          wallet.publicKey.toBytes(),
        ],
        program.programId,
      );

      let userStakeInfo = await program.account.stakeInfo.fetch(userStakePda);
      console.log('Claimable: ', userStakeInfo.totalClaim.toNumber());
      return userStakeInfo;
    } catch (error) {
      console.log('getClaimableToken error', error);
      return {
        totalClaim: new BN(0),
      };
    }
  };

  claimTokenTx = async (wallet: WalletContextState, tokenAddress: string) => {
    const provider = anchor.getProvider();

    try {
      const program = new Program(
        pumpProgramInterface as DefaiLaunchpad,
        provider,
      ) as Program<DefaiLaunchpad>;

      if (!provider.connection || !wallet.publicKey) {
        console.log('Warning: Wallet not connected');
        return;
      }

      const { isSupport } = getSupportTokenDefAIOld(tokenAddress.toString());
      const method = isSupport ? 'claimTokenLegacy' : 'claimToken';

      let params = {
        tokenMint: toPublicKey(tokenAddress),
        receiver: wallet.publicKey,
      };

      if (isSupport) {
        params['tokenProgram'] = TOKEN_PROGRAM_ID;
      }

      const tx = await program.methods[method]().accounts(params).transaction();

      tx.feePayer = wallet.publicKey;
      tx.recentBlockhash = (
        await provider.connection.getLatestBlockhash()
      ).blockhash;

      if (wallet.signTransaction) {
        const signedTx = await wallet.signTransaction(tx);
        const sTx = signedTx.serialize();
        const signature = await provider.connection.sendRawTransaction(sTx, {
          preflightCommitment: 'confirmed',
          skipPreflight: false,
        });
        const blockhash = await provider.connection.getLatestBlockhash();

        const res = await provider.connection.confirmTransaction(
          {
            signature,
            blockhash: blockhash.blockhash,
            lastValidBlockHeight: blockhash.lastValidBlockHeight,
          },
          'confirmed',
        );

        console.log('Successfully locking token.\n Signature: ', signature);
        return res;
      }
    } catch (error) {
      console.log('Error in locking token transaction', error, error.error);

      if (!provider) {
        return;
      }
      const { transaction = '', result } =
        (await handleTransaction({
          error,
          connection: provider.connection,
        })) || {};

      if (result?.value?.confirmationStatus) {
        console.log('----confirm----', { transaction, result });
        return { transaction, result };
      }
    }
  };

  async getStakeListByUser(wallet: WalletContextState) {
    try {
      const provider = anchor.getProvider();
      const program = new Program(
        pumpProgramInterface as DefaiLaunchpad,
        provider,
      ) as Program<DefaiLaunchpad>;

      let list = [];

      if (!provider.connection || !wallet.publicKey) {
        console.log('Warning: Wallet not connected');
        return;
      }

      const stakeList = await provider.connection.getParsedProgramAccounts(
        program.programId,
        {
          commitment: 'confirmed',
          filters: [
            {
              dataSize: 105,
            },
            {
              memcmp: {
                offset: 73, // number of bytes
                bytes: wallet.publicKey.toBase58(), // base58 encoded string
              },
            },
          ],
        },
      );

      for (const item of stakeList) {
        const info = program.coder.accounts.decode<
          anchor.IdlAccounts<DefaiLaunchpad>['stakeInfo']
        >('stakeInfo', item.account.data as Buffer);
        list = [...list, info];
      }
      return list;
    } catch (error) {
      console.log('Error in get stake list transaction', error, error.error);

      return [];
    }
  }

  async getMapVaultWithToken() {
    try {
      const provider = anchor.getProvider();
      const program = new Program(
        pumpProgramInterface as DefaiLaunchpad,
        provider,
      ) as Program<DefaiLaunchpad>;

      let list = [];

      if (!provider.connection) {
        console.log('Warning: Connection not connected');
        return;
      }

      const vaultList = await provider.connection.getParsedProgramAccounts(
        program.programId,
        {
          commitment: 'confirmed',
          filters: [
            {
              dataSize: 66,
            },
          ],
        },
      );

      for (const item of vaultList) {
        const vault = item.pubkey;

        const info = program.coder.accounts.decode<
          anchor.IdlAccounts<DefaiLaunchpad>['stakingVault']
        >('stakingVault', item.account.data as Buffer);
        list = [...list, { ...info, vault }];
      }
      return list;
    } catch (error) {
      console.log('Error in get stake list transaction', error, error.error);

      return [];
    }
  }
}
